import React, { useEffect, useState } from 'react'
import DatePicker from "react-datepicker";
import useShipDate from './useShipDate';
import { toast } from 'react-toastify';
import Modal from 'react-modal';
import AddEditForm from '../../../sharedComponents/AddEditForm';
import { PencilIcon, TrashIcon, CheckCircleIcon } from '@heroicons/react/24/solid'
import { FlightTakeoff, Sailing, Warehouse } from '@mui/icons-material'
import DeleteConfirm from '../../../sharedComponents/DeleteConfirm';
import { formatDate } from '../../../utils/dateUtils';
import { formatAmount } from '../../../utils/formatAmount';
import moment from 'moment-timezone';
import { Checkbox, FormControlLabel } from '@mui/material';
import WarehouseReceivingAddressService from '../warehouseReceivingAddress/WarehouseReceivingAddressService';

const customStyles = {
  content: {
    top: '50%',
    left: '50%',
    right: 'auto',
    bottom: 'auto',
    maxHeight: '90vh',
    width: '500px',
    marginRight: '-50%',
    transform: 'translate(-50%, -50%)',
    overflowY: 'auto',
    overflowX: 'hidden',
  },
};

const baseItems = [
{
  title: 'รอบเรือ',
  name: 'shipDate',
  type: 'date',
},
{
  title: 'ถึงไทย (ประมาณ)',
  name: 'expectThaiDate',
  type: 'date',
},
{
  title: 'ถึงไทย',
  name: 'thaiDate',
  type: 'date',
},
{
  title: 'มารับเอง',
  name: 'pickupDate',
  type: 'dateArray',
},
{
  title: 'ส่งของออก',
  name: 'thaiDeliveryDate',
  type: 'dateArray',
},
{
  title: 'น่ำหนัก จากคลัง',
  name: 'warehouseWeight',
  type: 'text',
},
{
  title: 'เป็น รอบหิ้วเครื่องบิน',
  name: 'isAir',
  type: 'checkbox',
},
{
  title: 'จ่ายเงินก่อนวันที่',
  name: 'paymentDueDate',
  type: 'date',
},
{
  title: 'วันเปลี่ยนที่อยู่สุดท้าย',
  name: 'addressChangeDeadlineDate',
  type: 'date',
},
];

const ShipDateSetting = () => {

  const [shipDate, setShipDate] = useState(new Date())
  const [shipDateState, setShipDateState] = useState({})
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [isAir, setIsAir] = useState(false);
  const [selectedAddressId, setSelectedAddressId] = useState('');
  const [warehouseAddresses, setWarehouseAddresses] = useState([]);

  const {
    shipDates,
    error,
    addShipDate,
    getShipDates,
    editShipDate,
    deleteShipDate
  } = useShipDate({ manage: true })

  const fetchWarehouseAddresses = async () => {
    try {
      const res = await WarehouseReceivingAddressService.getAll();
      setWarehouseAddresses(res.data || []);
    } catch (e) {
      console.error('Failed to load warehouse addresses', e);
    }
  };

  useEffect(() => {
    fetchWarehouseAddresses();
  }, []);

  const addressOptions = [
    { value: '', label: '-- ไม่มี (ปกติ) --' },
    ...warehouseAddresses.map(a => ({
      value: String(a.id),
      label: a.addressNickName,
    })),
  ];

  const warehouseSelectItem = {
    title: 'Warehouse Receiving Address (เฉพาะรอบเครื่องบิน)',
    name: 'warehouseReceivingAddressId',
    type: 'select',
    options: addressOptions,
  };

  const editFormItems = [
    ...baseItems,
    ...(shipDateState.isAir ? [warehouseSelectItem] : []),
  ];

  // Helper function to convert date string to Date object
  const parseDate = (dateValue) => {
    if (!dateValue) return null;
    if (dateValue instanceof Date) {
      // Check if it's a valid date
      return isNaN(dateValue.getTime()) ? null : dateValue;
    }
    if (typeof dateValue === 'string') {
      const parsed = moment(dateValue).tz('Asia/Bangkok').toDate();
      return isNaN(parsed.getTime()) ? null : parsed;
    }
    return null;
  };

  // Convert backend date strings to Date objects when loading data for editing
  const convertDatesToDateObjects = (data) => {
    if (!data || Object.keys(data).length === 0) return data;
    
    const converted = { ...data };
    
    // Convert single date fields
    ['shipDate', 'expectThaiDate', 'thaiDate', 'paymentDueDate', 'addressChangeDeadlineDate'].forEach(field => {
      if (converted[field]) {
        converted[field] = parseDate(converted[field]);
      }
    });
    
    // Convert date arrays
    if (converted.pickupDate) {
      if (Array.isArray(converted.pickupDate)) {
        converted.pickupDate = converted.pickupDate.map(date => parseDate(date)).filter(d => d !== null);
      } else {
        const parsed = parseDate(converted.pickupDate);
        converted.pickupDate = parsed ? [parsed] : [];
      }
    } else {
      converted.pickupDate = [];
    }
    
    if (converted.thaiDeliveryDate) {
      if (Array.isArray(converted.thaiDeliveryDate)) {
        converted.thaiDeliveryDate = converted.thaiDeliveryDate.map(date => parseDate(date)).filter(d => d !== null);
      } else {
        const parsed = parseDate(converted.thaiDeliveryDate);
        converted.thaiDeliveryDate = parsed ? [parsed] : [];
      }
    } else {
      converted.thaiDeliveryDate = [];
    }
    
    if (converted.warehouseReceivingAddressId != null) {
      converted.warehouseReceivingAddressId = String(converted.warehouseReceivingAddressId);
    } else {
      converted.warehouseReceivingAddressId = '';
    }

    return converted;
  };

  // Function to add a new date to an array
  const addDateToArray = (fieldName) => {
    setShipDateState(prev => ({
      ...prev,
      [fieldName]: [...(prev[fieldName] || []), new Date()]
    }));
  };

  // Function to remove a date from an array
  const removeDateFromArray = (fieldName, index) => {
    setShipDateState(prev => ({
      ...prev,
      [fieldName]: prev[fieldName].filter((_, i) => i !== index)
    }));
  };

  // Function to update a specific date in an array
  const updateDateInArray = (fieldName, index, newDate) => {
    setShipDateState(prev => ({
      ...prev,
      [fieldName]: prev[fieldName].map((date, i) => i === index ? newDate : date)
    }));
  };

  // Combined function for AddEditForm date array handling
  const handleDateArrayChange = (fieldName, action, index, newDate) => {
    if (action === 'add') {
      addDateToArray(fieldName);
    } else if (action === 'remove') {
      removeDateFromArray(fieldName, index);
    } else if (action === 'update') {
      updateDateInArray(fieldName, index, newDate);
    }
  };

  const handleRegisShipDateChange = (date) => {
    setShipDate(date)
	};

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
  
    if (type === "checkbox") {
      setShipDateState(prevState => {
        const next = { ...prevState, [name]: checked };
        if (name === 'isAir' && !checked) {
          next.warehouseReceivingAddressId = '';
        }
        return next;
      });
    } else {
      setShipDateState(prevState => ({ ...prevState, [name]: value }));
    }
  };
  
  const handleDateChange = (date, name) => {
    setShipDateState(prevState => ({ ...prevState, [name]: date }));
  };

// Simple function to check for duplicate dates in an array
const hasDuplicateDates = (dates) => {
  if (!Array.isArray(dates) || dates.length <= 1) return false;
  
  const dateStrings = dates.map(date => moment(date).tz('Asia/Bangkok').format('YYYY-MM-DD'));
  const uniqueDates = new Set(dateStrings);
  
  return dateStrings.length !== uniqueDates.size;
};

// Update handleShipDateUpdate to check before submitting
const handleShipDateUpdate = async () => {
  try {
    // Check for duplicate dates before submitting
    if (hasDuplicateDates(shipDateState.thaiDeliveryDate)) {
      toast.error('ส่งของออก: มีวันที่ซ้ำกัน');
      return;
    }
    
    if (hasDuplicateDates(shipDateState.pickupDate)) {
      toast.error('มารับเอง: มีวันที่ซ้ำกัน');
      return;
    }

    // Helper function using moment.js with Thailand timezone
    const toThailandDateString = (date) => {
      if (!date) return null;
      return moment(date).tz('Asia/Bangkok').format('YYYY-MM-DD');
    };

    const addressVal = shipDateState.warehouseReceivingAddressId;
    const dataToSend = {
      ...shipDateState,
      shipDate: toThailandDateString(shipDateState.shipDate),
      expectThaiDate: toThailandDateString(shipDateState.expectThaiDate),
      thaiDate: toThailandDateString(shipDateState.thaiDate),
      thaiDeliveryDate: Array.isArray(shipDateState.thaiDeliveryDate) 
        ? shipDateState.thaiDeliveryDate.map(date => toThailandDateString(date))
        : toThailandDateString(shipDateState.thaiDeliveryDate),
      pickupDate: Array.isArray(shipDateState.pickupDate)
        ? shipDateState.pickupDate.map(date => toThailandDateString(date))
        : toThailandDateString(shipDateState.pickupDate),
      paymentDueDate: toThailandDateString(shipDateState.paymentDueDate),
      addressChangeDeadlineDate: toThailandDateString(shipDateState.addressChangeDeadlineDate),
      warehouseReceivingAddressId:
        shipDateState.isAir && addressVal ? Number(addressVal) : null,
    };

      console.log(dataToSend);
      const response = await editShipDate(shipDateState.uuid, dataToSend);
      console.log(response)
      if(!response) 
        toast.error(error)
      else{
        toast.success('Ship Date updated successfully!');
        closeForm();
      }
    } catch (error) {
      const errorMessage = error.response?.data?.message || "An unexpected error occurred.";
      toast.error(errorMessage);
    }
  }

  const handleRegisterClick = async () => {
    const dateString = moment(shipDate).tz('Asia/Bangkok').format('YYYY-MM-DD');
    const payload = {
      shipDate: dateString,
      isAir: isAir,
      ...(isAir && selectedAddressId ? { warehouseReceivingAddressId: Number(selectedAddressId) } : {}),
    };
    const resultSuccess = await addShipDate(payload);
    if(!resultSuccess) 
      toast.error(error)
  }

  // ยกเลิกรอบวันหยุด = ปิด active (ไม่ลบ) — รอบยังอยู่ scheduler จึงไม่สร้างซ้ำ และ
  // หายจากหน้าจอเลือกรอบ/ปฏิทินลูกค้า กดเปิดใช้ใหม่ได้ถ้ายกเลิกผิด
  // (กรณีเลื่อนวัน: ยกเลิกอันเดิม แล้วกด "เพิ่มรอบส่ง" วันใหม่เอง)
  const handleToggleActive = async (shippingDate) => {
    const nextActive = shippingDate.active === false; // ถูกยกเลิกอยู่ -> เปิดใช้, ไม่งั้น -> ยกเลิก
    const resultSuccess = await editShipDate(shippingDate.uuid, { active: nextActive });
    if (!resultSuccess) {
      toast.error(error);
    } else {
      toast.success(nextActive ? 'เปิดใช้รอบแล้ว' : 'ยกเลิกรอบ (วันหยุด) แล้ว');
    }
  };

  const handleDeleteShipDate = async (id) => {
    try {
      const resultSuccess = await deleteShipDate(id);
      if(!resultSuccess) 
        toast.error(error)
      else{
        toast.success('Ship Date deleted successfully!');
      }
      closeConfirm();
    } catch (error) {
      const errorMessage = error.response?.data?.message || "An unexpected error occurred.";
      toast.error(errorMessage);
    }
  }

  const openForm = async (isEdit, uuid) => {
    console.log(uuid)
    if(isEdit){
      const shipDateToEdit = shipDates.find(w => w.uuid === uuid);
      if(shipDateToEdit) {
        // Convert date strings to Date objects for the form
        const convertedData = convertDatesToDateObjects(shipDateToEdit);
        setShipDateState(convertedData);
      }
    } else {
      // Clear state when adding new
      setShipDateState({});
    }
    setIsFormOpen(true);
  }

  const closeForm = () =>{
    setIsFormOpen(false);
  }

  const openConfirm = async (uuid) => {
    const shipDateToDelete = shipDates.find(w => w.uuid === uuid);
    if(shipDateToDelete) {
      setShipDateState(shipDateToDelete);
      setIsConfirmOpen(true) 
    }
  };

  const closeConfirm = () =>{
    setIsConfirmOpen(false)
  }

  useEffect(() => {
    getShipDates();
  }, []);

  useEffect(() => {
    toast.error(error)
  }, [error]);
  
  return (
    <div className="px-4 sm:px-6 lg:px-8">
      <Modal
        isOpen={isFormOpen}
        onRequestClose={closeForm}
        style={customStyles}
        ariaHideApp={false}
      >
        <AddEditForm 
          items={editFormItems} 
          name="แก้ไขรายรอบเรือ"
          data={shipDateState} 
          mode='edit'
          closeForm={closeForm}
          handleInputChange={handleInputChange}
          handleUpdate={handleShipDateUpdate}
          handleDateChange={handleDateChange}
          handleDateArrayChange={handleDateArrayChange} // Add this new prop
          stateValue={shipDateState}
        />
      </Modal>
      <Modal
        isOpen={isConfirmOpen}
        onRequestClose={closeConfirm}
        style={customStyles}
        ariaHideApp={false}
      >
        <DeleteConfirm 
          items={baseItems} 
          data={shipDateState} 
          closeConfirm={closeConfirm}
          confirmFunction={handleDeleteShipDate}
          formatDate={formatDate}
        />
      </Modal>
      <div className="sm:flex sm:items-center">
        <div className="mt-4 sm:ml-8 sm:mt-0 w-full">
        <h2 className='text-5xl'>Weekly Ship Date Setting</h2>
        <div className='flex items-end ml-auto'>
          <DatePicker 
            selected={shipDate}
            onChange={handleRegisShipDateChange}
            dateFormat={"yyyy-MM-dd"}
            className='datepicker2'
            />
          
          <FormControlLabel
            control={
              <Checkbox
                checked={isAir}
                onChange={(e) => {
                  const checked = e.target.checked;
                  setIsAir(checked);
                  if (!checked) setSelectedAddressId('');
                }}
                color="primary"
              />
            }
            label="Air"
            sx={{ ml: 2 }}
          />

          {isAir && (
          <select
            value={selectedAddressId}
            onChange={(e) => setSelectedAddressId(e.target.value)}
            className="ml-2 px-3 py-2 border border-gray-400 rounded-md text-sm"
          >
            <option value="">ทั่วไป (พี่โซ่)</option>
            {warehouseAddresses.map(a => (
              <option key={a.id} value={String(a.id)}>
                {a.addressNickName}
              </option>
            ))}
          </select>
          )}
          
          <button
            type="button"
            onClick={handleRegisterClick}
            className="whitespace-nowrap ml-2 rounded-md bg-indigo-600 px-3 py-2 text-center text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
            >
            Add Ship Date
          </button>
          </div>
        </div>
      </div>
      <div className="mt-6 flow-root">
        <div className="-mx-4 -my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
          <div className="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8">
            <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 sm:rounded-lg">
              <table className="min-w-full divide-y divide-gray-300">
                <thead className="bg-gray-50">
                  <tr>
                    <th scope="col" className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6">
                      Type
                    </th>
                    <th scope="col" className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6">
                      รอบเรือ
                    </th>
                    <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                      ของถึงไทย (ประมาณ)
                    </th>
                    <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                      ของถึงไทย
                    </th>
                    <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                      ส่งของออก
                    </th>
                    <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                      มารับเอง
                    </th>
                    <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                      น้ำหนักคลัง
                    </th>
                    <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                      จ่ายเงินก่อนวันที่
                    </th>
                    <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                      วันเปลี่ยนที่อยู่สุดท้าย
                    </th>
                    <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                      Warehouse Address
                    </th>
                    <th scope="col" className="relative py-3.5 pl-3 pr-4 sm:pr-6">
                      <span className="sr-only">Edit</span>
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 bg-white">
                  {shipDates.map((shippingDate) => (
                    <tr key={shippingDate.uuid} className={shippingDate.active === false ? 'opacity-50 bg-gray-50' : ''}>
                      <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900 sm:pl-6">
                        <div className="flex items-center justify-center">
                          {shippingDate.isAir ? (
                            <FlightTakeoff className="w-6 h-6 text-blue-600" title="Air Transport" />
                          ) : (
                            <Sailing className="w-6 h-6 text-green-600" title="Sea Transport" />
                          )}
                        </div>
                      </td>
                      <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900 sm:pl-6">
                        {formatDate(shippingDate.shipDate)}
                        {shippingDate.active === false && (
                          <span className="ml-2 inline-flex px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700">
                            ยกเลิก (วันหยุด)
                          </span>
                        )}
                      </td>
                      <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">{formatDate(shippingDate.expectThaiDate)}</td>
                      <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">{formatDate(shippingDate.thaiDate)}</td>
                      <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                        {Array.isArray(shippingDate.thaiDeliveryDate) 
                          ? shippingDate.thaiDeliveryDate.map(date => formatDate(date)).join(', ')
                          : formatDate(shippingDate.thaiDeliveryDate)
                        }
                      </td>
                      <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                        {Array.isArray(shippingDate.pickupDate) 
                          ? shippingDate.pickupDate.map(date => formatDate(date)).join(', ')
                          : formatDate(shippingDate.pickupDate)
                        }
                      </td>
                      <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">{formatAmount(shippingDate.warehouseWeight)}</td>
                      <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">{formatDate(shippingDate.paymentDueDate)}</td>
                      <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">{formatDate(shippingDate.addressChangeDeadlineDate)}</td>
                      <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                        {shippingDate.isAir && shippingDate.warehouseReceivingAddress ? (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800">
                            <Warehouse sx={{ fontSize: 14 }} />
                            {shippingDate.warehouseReceivingAddress.addressNickName}
                          </span>
                        ) : '—'}
                      </td>
                      <td className="relative whitespace-nowrap py-4 pl-3 pr-4 text-right text-sm font-medium sm:pr-6">
                        <div className="flex items-center justify-end space-x-3">
                            {shippingDate.active === false ? (
                              <button
                                type="button"
                                onClick={() => handleToggleActive(shippingDate)}
                                className="text-xs font-semibold text-green-700 hover:underline"
                              >
                                เปิดใช้
                              </button>
                            ) : (
                              <button
                                type="button"
                                onClick={() => handleToggleActive(shippingDate)}
                                className="text-xs font-semibold text-orange-600 hover:underline"
                              >
                                ยกเลิก
                              </button>
                            )}
                            <PencilIcon onClick={()=> openForm(true, shippingDate.uuid)} className='cursor-pointer flex items-center w-6 h-6 fill-green-600'/>
                            <TrashIcon onClick={()=> openConfirm(shippingDate.uuid)} className='cursor-pointer flex items-center w-6 h-6 fill-red-600'/>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default ShipDateSetting